/**
 * LLM Provider Mode Service
 * ---------------------------------------------------------------------------
 * End-user choice between the *Commercial* LLM (Claude / GPT — consumes AI
 * credits) and the *Open-Source* LLM (Mistral / Llama — free, 0 credit).
 *
 * Backed by billing-service:
 *   GET  /api/v1/credits/provider-mode/{tenant}/{user}?estimated_amount=N
 *   PUT  /api/v1/credits/provider-mode/{tenant}/{user}  { mode, estimated_amount }
 *
 * The mode maps to the existing CreditStrategy (commercial -> AUTO_OPTIMAL,
 * oss -> OPEN_SOURCE_ONLY) which the ai-orchestrator model_router already
 * honours, so switching the mode actually re-routes the LLM.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { get, put } from '@/lib/api/client'
import { getTenantId, getUserId } from '@/lib/auth/keycloak'

export type LlmMode = 'commercial' | 'oss' | 'auto'

export interface ProviderModeStatus {
  mode: LlmMode
  strategy: string
  on_exhaustion?: string
  estimated_needed: number
  commercial_available: boolean
  commercial_credits: number
  /** GPU inference enabled by the platform admin (required for OSS). */
  gpu_enabled?: boolean
  oss_available: boolean
  /** Open-source model served in OSS mode (e.g. "qwen3-14b"). */
  oss_model?: string
  /** Can the ACTIVE mode run an AI action without user intervention? */
  sufficient: boolean
  warning: string | null
  /** Mode to fall back to when the active mode is insufficient (usually "oss"). */
  suggested_mode: LlmMode | null
}

const FALLBACK_STATUS: ProviderModeStatus = {
  mode: 'commercial',
  strategy: 'AUTO_OPTIMAL',
  on_exhaustion: 'PAUSE',
  estimated_needed: 5,
  commercial_available: true,
  commercial_credits: 0,
  gpu_enabled: true,
  oss_available: true,
  oss_model: 'qwen3-14b',
  sufficient: true,
  warning: null,
  suggested_mode: null,
}

export const llmModeKeys = {
  all: ['llm-mode'] as const,
  status: (estimated: number) => [...llmModeKeys.all, 'status', estimated] as const,
}

function ids(): { tenant: string; user: string } | null {
  const tenant = getTenantId()
  const user = getUserId()
  if (!tenant || !user) return null
  return { tenant, user }
}

/** Imperative fetch — used by the pre-action credit guard (not a hook). */
export async function fetchProviderStatus(estimated = 5): Promise<ProviderModeStatus> {
  const id = ids()
  if (!id) return FALLBACK_STATUS
  try {
    // Path is relative to the API base (/api/v1 is added by the axios baseURL in
    // cloud-gateway mode, or by resolveApiUrl in cloud-direct mode).
    return await get<ProviderModeStatus>(
      `/credits/provider-mode/${id.tenant}/${id.user}?estimated_amount=${estimated}`,
    )
  } catch {
    // Never block the user on a billing-service hiccup — degrade open.
    return FALLBACK_STATUS
  }
}

export async function setProviderMode(
  mode: LlmMode,
  estimated = 5,
): Promise<ProviderModeStatus> {
  const id = ids()
  if (!id) return { ...FALLBACK_STATUS, mode }
  return await put<ProviderModeStatus>(
    `/credits/provider-mode/${id.tenant}/${id.user}`,
    { mode, estimated_amount: estimated },
  )
}

export function useProviderMode(estimated = 5) {
  return useQuery({
    queryKey: llmModeKeys.status(estimated),
    queryFn: () => fetchProviderStatus(estimated),
    staleTime: 30_000,
  })
}

export function useSetProviderMode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mode, estimated }: { mode: LlmMode; estimated?: number }) =>
      setProviderMode(mode, estimated ?? 5),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: llmModeKeys.all })
    },
  })
}

/**
 * Lightweight shared store so the Copilot send-handler (and any AI trigger)
 * can read the latest mode/sufficiency that the selector last observed,
 * without prop-drilling. The selector keeps this fresh.
 */
interface LlmModeStore {
  status: ProviderModeStatus
  setStatus: (s: ProviderModeStatus) => void
}

export const useLlmModeStore = create<LlmModeStore>((set) => ({
  status: FALLBACK_STATUS,
  setStatus: (status) => set({ status }),
}))
